import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt, faCheckCircle, faExclamationTriangle, faQuestionCircle,
  faExternalLinkAlt, faGavel, faUserTie, faClipboardCheck, faSatelliteDish,
  faFlag,
} from '@fortawesome/free-solid-svg-icons';
import './VesselIntelligencePanel.css';
import { globeService } from '../../services/globeService';

/**
 * Expanded-card Risk & Compliance card for a hull.
 *
 * Renders the full screening picture from the R&C product: sanctions hits
 * (with the sanction list as evidence), ownership & management (incl. class
 * society / P&I club standing), Port State Control record, operational
 * signals (AIS gaps/spoofs, dark activity, STS) and flag risk. Voyage
 * predictions (ETA / port calls) appear only when that separate product is
 * enabled. Config-gated end-to-end; no vendor name appears anywhere.
 */
function fmtDate(value) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch (e) {
    return String(value);
  }
}

function fmtYear(value) {
  if (!value) return null;
  const y = String(value).slice(0, 4);
  return /^\d{4}$/.test(y) ? y : null;
}

const STATUS_META = {
  clean: { label: 'Clear', cls: 'rc-pill-clean', icon: faCheckCircle },
  warning: { label: 'Warning', cls: 'rc-pill-warning', icon: faExclamationTriangle },
  flagged: { label: 'Flagged', cls: 'rc-pill-flagged', icon: faExclamationTriangle },
  unknown: { label: 'Unknown', cls: 'rc-pill-unknown', icon: faQuestionCircle },
};

const HIT_KIND_LABELS = {
  vessel: 'Vessel',
  company: 'Company',
  flag: 'Flag',
  cargo: 'Cargo',
  trade: 'Trade',
};

const SIGNAL_DEFS = [
  { key: 'ais_gaps', label: 'AIS gaps' },
  { key: 'ais_spoofs', label: 'AIS spoofing' },
  { key: 'dark_port_calls', label: 'Dark port calls' },
  { key: 'dark_sts_events', label: 'Dark STS transfers' },
  { key: 'id_manipulations', label: 'Identity manipulation' },
  { key: 'sts_events', label: 'STS transfers' },
];

const OWNERSHIP_DEFS = [
  { key: 'beneficial_owner', label: 'Beneficial owner' },
  { key: 'registered_owner', label: 'Registered owner' },
  { key: 'commercial_manager', label: 'Commercial mgr' },
  { key: 'ism_manager', label: 'ISM manager' },
];

function SectionTitle({ icon, children }) {
  return (
    <span className="rc-section-title">
      <FontAwesomeIcon icon={icon} className="rc-section-title-icon" />
      {children}
    </span>
  );
}

function VesselIntelligencePanel({ imo }) {
  const [compliance, setCompliance] = useState({ loading: true });
  const [risk, setRisk] = useState({ loading: true });
  const [eta, setEta] = useState({ loading: true });
  const [calls, setCalls] = useState({ loading: true });

  useEffect(() => {
    let active = true;
    if (!imo) {
      setCompliance({ loading: false });
      setRisk({ loading: false });
      setEta({ loading: false });
      setCalls({ loading: false });
      return undefined;
    }
    setCompliance({ loading: true });
    setRisk({ loading: true });
    setEta({ loading: true });
    setCalls({ loading: true });

    globeService
      .getVesselCompliance(imo)
      .then((r) => { if (active) setCompliance({ loading: false, ...(r || {}) }); })
      .catch(() => { if (active) setCompliance({ loading: false, error: true }); });
    globeService
      .getVesselRisk(imo)
      .then((r) => { if (active) setRisk({ loading: false, ...(r || {}) }); })
      .catch(() => { if (active) setRisk({ loading: false, error: true }); });
    globeService
      .getVesselSmartEta(imo)
      .then((r) => { if (active) setEta({ loading: false, ...(r || {}) }); })
      .catch(() => { if (active) setEta({ loading: false, error: true }); });
    globeService
      .getVesselPortCalls(imo, 5)
      .then((r) => { if (active) setCalls({ loading: false, ...(r || {}) }); })
      .catch(() => { if (active) setCalls({ loading: false, error: true }); });
    return () => { active = false; };
  }, [imo]);

  if (!imo) return null;

  if (compliance.loading || risk.loading) {
    return <div className="rc-card rc-card-loading">Loading risk &amp; compliance…</div>;
  }

  const screeningOn = Boolean(compliance.configured);
  const voyageOn = Boolean(eta.configured || calls.configured);
  if (!screeningOn && !voyageOn) return null;

  // ── Status ──
  const statusKey =
    compliance.sanctions_status === 'clean' && compliance.warning
      ? 'warning'
      : compliance.sanctions_status;
  const statusMeta = STATUS_META[statusKey] || STATUS_META.unknown;

  // ── Data blocks ──
  const vessel = compliance.vessel || {};
  const riskData = risk.configured ? risk.data || {} : {};
  const riskScreened = Boolean(riskData.screened);
  const counts = riskData.counts || {};
  const detail = riskData.detail || {};
  const hits = detail.sanction_hits || [];
  const flagRisks = detail.flag || {};
  const classSociety = (detail.class_societies || [])[0];
  const pniClub = (detail.pni_clubs || [])[0];
  const inspections = detail.inspections || [];
  const ubo = detail.ubo || '';

  const ownershipRows = OWNERSHIP_DEFS.filter((d) => vessel[d.key]);
  const signalRows = SIGNAL_DEFS.filter((d) => (counts[d.key] || 0) > 0);
  const flagChips = [
    flagRisks.flag_of_convenience && 'Flag of convenience',
    flagRisks.uscg_targeted && 'USCG-targeted flag',
    flagRisks.false_flag && 'False flag',
  ].filter(Boolean);

  const detentions = counts.psc_detentions || 0;
  const bannings = counts.psc_bannings || 0;
  const inspectionCount = counts.psc_inspections || 0;

  const etaData = eta.configured ? eta.data || {} : {};
  const portCalls = calls.configured ? (calls.data || []).filter(Boolean) : [];

  return (
    <div className="rc-card">
      <div className="rc-card-header">
        <span className="rc-card-title">
          <FontAwesomeIcon icon={faShieldAlt} />
          Risk &amp; Compliance
        </span>
        <span className="rc-card-header-right">
          {screeningOn && (
            <span className={`rc-pill ${statusMeta.cls}`}>
              <FontAwesomeIcon icon={statusMeta.icon} />
              {statusMeta.label}
            </span>
          )}
          {compliance.screened_at && (
            <span className="rc-screened-at">screened {fmtDate(compliance.screened_at)}</span>
          )}
        </span>
      </div>

      {screeningOn && (
        <div className="rc-card-grid">
          {/* ── Sanctions ── */}
          <div className="rc-section">
            <SectionTitle icon={faGavel}>Sanctions</SectionTitle>
            {statusKey === 'flagged' && hits.length > 0 ? (
              <ul className="rc-hits">
                {hits.map((h, i) => (
                  <li key={`${h.kind}-${h.name}-${i}`} className="rc-hit">
                    <span className="rc-hit-kind">{HIT_KIND_LABELS[h.kind] || h.kind}</span>
                    <span className="rc-hit-name">{h.name || '—'}</span>
                    {h.list_name && (
                      h.list_url ? (
                        <a
                          className="rc-hit-list"
                          href={h.list_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Open the sanction list entry"
                        >
                          {h.list_name}
                          <FontAwesomeIcon icon={faExternalLinkAlt} />
                        </a>
                      ) : (
                        <span className="rc-hit-list rc-hit-list-plain">{h.list_name}</span>
                      )
                    )}
                    {h.since && <span className="rc-hit-date">since {fmtDate(h.since)}</span>}
                  </li>
                ))}
              </ul>
            ) : statusKey === 'flagged' ? (
              <div className="rc-note rc-note-bad">
                Sanctions exposure detected
                {(compliance.sanction_lists_matched || []).length > 0
                  ? ` — ${compliance.sanction_lists_matched.join(', ')}`
                  : ''}.
              </div>
            ) : statusKey === 'unknown' ? (
              <div className="rc-note">Screening result could not be determined.</div>
            ) : (
              <div className="rc-note rc-note-good">
                <FontAwesomeIcon icon={faCheckCircle} />
                No matches — vessel and ownership chain screened against OFAC,
                EU, UN, OFSI &amp; FCDO lists.
              </div>
            )}
            {statusKey === 'warning' && (
              <div className="rc-note rc-note-warn">
                Risk indicators detected — see signals and PSC record.
              </div>
            )}
          </div>

          {/* ── Ownership & management ── */}
          <div className="rc-section">
            <SectionTitle icon={faUserTie}>Ownership &amp; management</SectionTitle>
            <dl className="rc-kv">
              {ownershipRows.map((d) => (
                <React.Fragment key={d.key}>
                  <dt>{d.label}</dt>
                  <dd>{vessel[d.key]}</dd>
                </React.Fragment>
              ))}
              {ubo && (
                <>
                  <dt>UBO</dt>
                  <dd>{ubo}</dd>
                </>
              )}
              {classSociety && (
                <>
                  <dt>Class society</dt>
                  <dd>
                    {classSociety.name}
                    {classSociety.is_iacs && <span className="rc-member-badge">IACS</span>}
                    {fmtYear(classSociety.since) && (
                      <span className="rc-since">since {fmtYear(classSociety.since)}</span>
                    )}
                  </dd>
                </>
              )}
              {pniClub && (
                <>
                  <dt>P&amp;I club</dt>
                  <dd>
                    {pniClub.name}
                    {pniClub.is_igpi && <span className="rc-member-badge">IGP&amp;I</span>}
                    {fmtYear(pniClub.since) && (
                      <span className="rc-since">since {fmtYear(pniClub.since)}</span>
                    )}
                  </dd>
                </>
              )}
            </dl>
            {ownershipRows.length === 0 && !classSociety && !pniClub && !ubo && (
              <div className="rc-note">No ownership data returned.</div>
            )}
          </div>

          {/* ── Port State Control ── */}
          <div className="rc-section">
            <SectionTitle icon={faClipboardCheck}>Port State Control</SectionTitle>
            <div className="rc-psc-stats">
              <span className={`rc-stat ${detentions > 0 ? 'rc-stat-bad' : ''}`}>
                <strong>{detentions}</strong> detentions
              </span>
              <span className={`rc-stat ${bannings > 0 ? 'rc-stat-bad' : ''}`}>
                <strong>{bannings}</strong> bannings
              </span>
              <span className="rc-stat">
                <strong>{inspectionCount}</strong> inspections
              </span>
            </div>
            {inspections.length > 0 ? (
              <ul className="rc-inspections">
                {inspections.slice(0, 3).map((ins, i) => (
                  <li key={`${ins.port}-${ins.date}-${i}`}>
                    <span className="rc-inspection-port">{ins.port || '—'}</span>
                    <span className="rc-inspection-meta">
                      {fmtDate(ins.date) || ''}
                      {ins.deficiencies > 0
                        ? ` · ${ins.deficiencies} deficienc${ins.deficiencies === 1 ? 'y' : 'ies'}`
                        : ' · no deficiencies'}
                      {ins.source ? ` · ${ins.source}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            ) : riskScreened ? (
              <div className="rc-note">No recent inspections on record.</div>
            ) : null}
          </div>

          {/* ── Operational signals + flag ── */}
          <div className="rc-section">
            <SectionTitle icon={faSatelliteDish}>Operational signals</SectionTitle>
            {riskScreened && signalRows.length > 0 ? (
              <ul className="rc-signals">
                {signalRows.map((d) => (
                  <li key={d.key}>
                    <span className="rc-signal-label">{d.label}</span>
                    <span className="rc-signal-count">{counts[d.key]}</span>
                  </li>
                ))}
              </ul>
            ) : riskScreened ? (
              <div className="rc-note rc-note-good">
                <FontAwesomeIcon icon={faCheckCircle} />
                No dark activity, spoofing or STS signals detected.
              </div>
            ) : (
              <div className="rc-note">Operational signals unavailable.</div>
            )}
            {flagChips.length > 0 && (
              <div className="rc-flag-chips">
                {flagChips.map((label) => (
                  <span key={label} className="rc-flag-chip">
                    <FontAwesomeIcon icon={faFlag} />
                    {label}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {voyageOn && (
        <div className="rc-card-grid rc-card-grid-voyage">
          <div className="rc-section">
            <SectionTitle icon={faSatelliteDish}>Predicted ETA</SectionTitle>
            {etaData.predicted_destination || etaData.predicted_eta ? (
              <div className="rc-eta">
                <strong>{etaData.predicted_destination || 'Destination unknown'}</strong>
                {etaData.predicted_eta ? <span> · {fmtDate(etaData.predicted_eta)}</span> : null}
              </div>
            ) : (
              <div className="rc-note">No prediction available.</div>
            )}
          </div>
          <div className="rc-section">
            <SectionTitle icon={faClipboardCheck}>Recent port calls</SectionTitle>
            {portCalls.length > 0 ? (
              <ul className="rc-inspections">
                {portCalls.map((p, i) => (
                  <li key={`${p.port || 'port'}-${i}`}>
                    <span className="rc-inspection-port">
                      {p.port || '--'}{p.country ? `, ${p.country}` : ''}
                    </span>
                    {p.arrival && <span className="rc-inspection-meta">{fmtDate(p.arrival)}</span>}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="rc-note">No recent port calls.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default VesselIntelligencePanel;
